import baseAPI from "@/api/base";
import {
  EditOrganizationSetting,
  GetAutoBucketSettings,
  GetDialerRotationSetting,
  GetOrganizationSetting,
  RegisterOrganizationSetting,
  UpdateAutoBucketSettings,
  UpdateDialerRotationSetting,
} from "@/dtos/OrganizationSetting.dto";
import {
  DELETE_ORGANIZATION_SETTING_URL,
  EDIT_ORGANIZATION_SETTING_URL,
  GET_AUTO_BUCKET_SETTINGS_URL,
  GET_DIALER_ROTATION_SETTING_URL,
  GET_ORGANIZATION_SETTING_URL,
  REGISTER_ORGANIZATION_SETTING_URL,
  TOGGLE_ORGANIZATION_SETTING_IS_ACTIVE_URL,
  UPDATE_AUTO_BUCKET_SETTINGS_URL,
  UPDATE_DIALER_ROTATION_SETTING_URL,
  UPDATE_PARENT_TEMPLATE_FOLDER_ORDER_URL,
} from "@/utils/urls";
import { AxiosResponse } from "axios";

class OrganizationSetting {
  static getOrganizationSettings = async (
    getOrganizationSetting: GetOrganizationSetting,
    retryCount = 0,
    maxRetries = 2
  ): Promise<AxiosResponse> => {
    try {
      const response: AxiosResponse = await baseAPI.get(GET_ORGANIZATION_SETTING_URL, {
        params: getOrganizationSetting,
        timeout: 15000,
      });
      return response;
    } catch (error: any) {
      console.error("Organization setting request failed:", error);

      if (
        (error.code === "ECONNABORTED" || error.message?.includes("timeout")) &&
        retryCount < maxRetries
      ) {
        const delay = Math.pow(2, retryCount) * 1000;

        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.getOrganizationSettings(getOrganizationSetting, retryCount + 1, maxRetries);
      }

      throw error;
    }
  };

  static registerOrganizationSetting = async (
    registerOrganizationSetting: RegisterOrganizationSetting
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(REGISTER_ORGANIZATION_SETTING_URL, {
      ...registerOrganizationSetting,
    });
    return response;
  };

  static editOrganizationSetting = async (
    editOrganizationSetting: EditOrganizationSetting
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(EDIT_ORGANIZATION_SETTING_URL, {
      ...editOrganizationSetting,
    });
    return response;
  };

  static toggleOrganizationSettingIsActive = async (
    editOrganizationSetting: EditOrganizationSetting
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.patch(TOGGLE_ORGANIZATION_SETTING_IS_ACTIVE_URL, {
      ...editOrganizationSetting,
    });
    return response;
  };

  static deleteOrganizationSetting = async (id: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.delete(
      `${DELETE_ORGANIZATION_SETTING_URL}/${id}`
    );
    return response;
  };

  static getAutoBucketSettings = async (
    getAutoBucketSettings: GetAutoBucketSettings
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_AUTO_BUCKET_SETTINGS_URL, {
      params: getAutoBucketSettings,
    });
    return response;
  };

  static updateAutoBucketSettings = async (
    updateAutoBucketSettings: UpdateAutoBucketSettings
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(UPDATE_AUTO_BUCKET_SETTINGS_URL, {
      ...updateAutoBucketSettings,
    });
    return response;
  };

  static getDialerRotationSetting = async (
    getDialerRotationSetting: GetDialerRotationSetting
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_DIALER_ROTATION_SETTING_URL, {
      params: getDialerRotationSetting,
    });
    return response;
  };

  static updateDialerRotationSetting = async (
    updateDialerRotationSetting: UpdateDialerRotationSetting
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(UPDATE_DIALER_ROTATION_SETTING_URL, {
      ...updateDialerRotationSetting,
    });
    return response;
  };

  static updateParentTemplateFolderOrder = async (
    parentTemplateFolderOrder: string[]
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(UPDATE_PARENT_TEMPLATE_FOLDER_ORDER_URL, {
      parentTemplateFolderOrder,
    });
    return response;
  };
}

export default OrganizationSetting;
